import utils from './utils';

let nslToken = '';
let memxNslToken = '';

const DEFAULT_NSL_TOKEN_TTL_MS = 30 * 60 * 1000;
/** Parent `SpaAuth.refreshAccessToken` issues a new token up to 5 minutes before the current one expires per NGP. */
const REFRESH_BEFORE_EXPIRY_MS = 180000; // 3 minutes before expiry, to allow for clock skew and network latency
const REFRESH_RETRY_DELAY_MS = 1_000;
const MAX_REFRESH_RETRY_ATTEMPTS = 2;
const REFRESH_REQUEST_TIMEOUT_MS = 30 * 1000;

/** JWT payload fields used across NSL token handling. */
export type JwtPayload = {
  exp?: number;
  iat?: number;
  sub?: string;
  tenant_id?: string;
  NortonGUID?: string;
};

type PendingRefresh = {
  reject: (_error: Error) => void;
  resolve: (_token: string) => void;
};

type SpaAuthBridge = {
  refreshAccessToken: (_onSuccess: (_token: string) => void, _onError: () => void) => void;
};

let refreshTimerId: ReturnType<typeof setTimeout> | null = null;
let pendingRefresh: PendingRefresh | null = null;

/**
 * Sets nslToken for use in methods requiring NSL tokens.
 * Should be set anytime a new NSL token is fetched.
 *
 * **Partner white-label flows** (`PARTNER_LOGIN`, `PARTNER_EXTEND_ACCESS`) must use this
 * helper only — the partner parent owns NSL refresh and pushes updated tokens via
 * `PARTNER_EXTEND_ACCESS`. Do not call `updateNslToken` for partner auth.
 *
 * @param token NSL token string
 */
export function setNslToken(token: string): void {
  nslToken = token;
}

/**
 * Returns the nsl token
 * @returns Nsl token
 */
export function getNslToken(): string {
  return nslToken;
}

export function setMemxNslToken(token: string): void {
  memxNslToken = token;
}

export function getMemxNslToken(): string {
  return memxNslToken;
}

/**
 * Best-effort decode of a JWT payload (no signature verification).
 * Used client-side only to read claims such as `exp`, `sub`, or `tenant_id`.
 */
export function parseJwtPayload(token: string): JwtPayload | null {
  const segments = token.split('.');
  if (segments.length < 2) {
    return null;
  }

  try {
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * Returns the token expiry instant in milliseconds since epoch.
 * Prefers the `exp` claim; falls back to `iat + fallbackTtlMs`, then `now + fallbackTtlMs`.
 */
export function getJwtExpiryMs(token: string, fallbackTtlMs = DEFAULT_NSL_TOKEN_TTL_MS): number {
  const payload = parseJwtPayload(token);
  const nowMs = Date.now();

  if (typeof payload?.exp === 'number' && Number.isFinite(payload.exp)) {
    return payload.exp * 1000;
  }
  if (typeof payload?.iat === 'number' && Number.isFinite(payload.iat)) {
    return payload.iat * 1000 + fallbackTtlMs;
  }
  return nowMs + fallbackTtlMs;
}

function clearRefreshTimer(): void {
  if (refreshTimerId !== null) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
}

/** Cancels any scheduled refresh and in-flight parent refresh request. */
export function stopNslTokenRefresh(): void {
  clearRefreshTimer();
  if (pendingRefresh) {
    pendingRefresh.reject(new Error('NSL token refresh cancelled'));
    pendingRefresh = null;
  }
}

/**
 * Stores the token and schedules post-expiry refresh for Norton NGP iframe flows only
 * (`REACT_APP_ENABLE_OIDC_TOKEN_EXCHANGE` + parent `SpaAuth.refreshAccessToken`).
 *
 * Not for partner auth — partners use `setNslToken` and `PARTNER_EXTEND_ACCESS` instead.
 */
export function updateNslToken(token: string): void {
  setNslToken(token);
  if (!token) {
    stopNslTokenRefresh();
    return;
  }
  if (typeof window !== 'undefined' && window.REACT_APP_ENABLE_OIDC_TOKEN_EXCHANGE === true) {
    scheduleNslTokenRefresh(token);
  }
}

/**
 * Schedules `SpaAuth.refreshAccessToken` before the JWT expires.
 * @param offsetBeforeExpiryMs Milliseconds before `exp` to trigger refresh (default 3 minutes).
 */
export function scheduleNslTokenRefresh(token: string, offsetBeforeExpiryMs = REFRESH_BEFORE_EXPIRY_MS): void {
  clearRefreshTimer();
  if (!token) {
    return;
  }

  const expiryMs = getJwtExpiryMs(token);
  const refreshAtMs = expiryMs - offsetBeforeExpiryMs;
  const delayMs = Math.max(0, refreshAtMs - Date.now());

  refreshTimerId = setTimeout(() => {
    refreshTimerId = null;
    void runNslTokenRefresh(token, 0);
  }, delayMs);
}

export function resolvePendingNslTokenRefresh(token: string): void {
  pendingRefresh?.resolve(token);
}

export function rejectPendingNslTokenRefresh(error: Error): void {
  pendingRefresh?.reject(error);
}

function tryParentSpaAuthRefresh(): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      const parentWindow = window.parent as Window & {SpaAuth?: SpaAuthBridge};
      if (!parentWindow?.SpaAuth || typeof parentWindow.SpaAuth.refreshAccessToken !== 'function') {
        resolve(null);
        return;
      }
      parentWindow.SpaAuth.refreshAccessToken(
        (token: string) => {
          resolve(token || null);
        },
        () => {
          resolve(null);
        }
      );
    } catch {
      resolve(null);
    }
  });
}

function requestNslAccessTokenRefreshViaPostMessage(): Promise<string> {
  return new Promise((resolve, reject) => {
    if (pendingRefresh) {
      reject(new Error('NSL token refresh already in progress'));
      return;
    }

    const timeoutId = setTimeout(() => {
      pendingRefresh = null;
      reject(new Error('NSL token refresh timed out'));
    }, REFRESH_REQUEST_TIMEOUT_MS);

    pendingRefresh = {
      resolve: (token: string) => {
        clearTimeout(timeoutId);
        pendingRefresh = null;
        resolve(token);
      },
      reject: (error: Error) => {
        clearTimeout(timeoutId);
        pendingRefresh = null;
        reject(error);
      },
    };

    utils.sendMessage({type: 'REFRESH_NSL_ACCESS_TOKEN', payload: {}}, window.REACT_APP_PARENT_HOST);
  });
}

async function requestNslAccessTokenRefresh(): Promise<string> {
  const directToken = await tryParentSpaAuthRefresh();
  if (directToken) {
    return directToken;
  }
  return requestNslAccessTokenRefreshViaPostMessage();
}

async function runNslTokenRefresh(expectedToken: string, retryAttempt: number): Promise<void> {
  const oldToken = getNslToken();
  try {
    const newToken = await requestNslAccessTokenRefresh();

    if (!newToken) {
      throw new Error('NSL token refresh returned empty token');
    }
    if (newToken === oldToken || newToken === expectedToken) {
      throw new Error('NSL token refresh returned the same token');
    }

    setNslToken(newToken);
    scheduleNslTokenRefresh(newToken, REFRESH_BEFORE_EXPIRY_MS);
  } catch (error) {
    if (retryAttempt < MAX_REFRESH_RETRY_ATTEMPTS) {
      clearRefreshTimer();
      refreshTimerId = setTimeout(() => {
        refreshTimerId = null;
        void runNslTokenRefresh(expectedToken, retryAttempt + 1);
      }, REFRESH_RETRY_DELAY_MS);
      return;
    }
    utils.log({error: error, message: '[NSL token refresh] failed after retries:'});
  }
}
